<template>
  <div v-show="this.$parent.getDisplayView() && displayChart" class="col-12 no-gutters container mt-3">
    <div class="ranks-chart-title-bar d-flex align-items-center">
      <img src="/img/i-blue.svg" width="22" height="22" alt="Info" class="mr-1" />
      <h4 class="text-primary mb-0">Rank Chart</h4>
    </div>
    <div class="e-chart-container mt-3">
      <div class="title-and-chart">
        <div :id="eChartId" class="e-chart ranks-needs-metrics-chart">
          <chart :options="chart.options"></chart>
        </div>
        <div :id="eChartMiniId">
          <img :src="imageUrl" class="pdf-content-image-width hidden-dashboard-display"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";

import EChartsMixins from "../../mixins/EChartsMixins";
import RanksService from "../../services/echarts/ranks.service";
import ScatterService from "../../services/echarts/scatter.service";
import ReportService from "../../services/report.service";
import SymbolService from "../../services/symbol.service";

export default {
  name: "RanksChartForNeedsAndMetrics",
  mixins: [EChartsMixins],
  data() {
    return {
      displayChart: false,
      chartData: {},
      inputDownloadFileName: "Ranks Needs and Metrics"
    };
  },
  props: {
    vAxisForPerformance: {
      type: [Array, Object],
      default: function() {
        return [];
      }
    },
    vAxisForMetric: {
      type: [Array, Object],
      default: function() {
        return [];
      }
    }
  },
  methods: {
    normalizeAxisLookupValue(value) {
      return String(value || "").toLowerCase().replace(/[^a-z0-9-]/g, "");
    },
    getAxisConfiguration(axisEntry) {
      if (typeof axisEntry == "undefined" || axisEntry == null) {
        return {};
      }
      return typeof axisEntry.configuration != "undefined" && axisEntry.configuration != null ? axisEntry.configuration : axisEntry;
    },
    getAxisEntryLookupValues(axisEntry) {
      let configuration = this.getAxisConfiguration(axisEntry);
      return [
        axisEntry.value,
        axisEntry.name,
        axisEntry.label,
        axisEntry.id,
        axisEntry.key,
        configuration.value,
        configuration.name,
        configuration.label,
        configuration.id,
        configuration.key
      ].map(value => value);
    },
    getSelectedPerformanceAxisConfiguration() {
      let selectedValue = this.chartData.needAxis.value;
      if (typeof selectedValue == "undefined" || selectedValue == null || selectedValue == "") {
        return {};
      }

      if (!Array.isArray(this.vAxisForPerformance)) {
        let axisConfiguration = this.vAxisForPerformance[selectedValue];
        return this.getAxisConfiguration(axisConfiguration);
      }

      let match = this.vAxisForPerformance.find(axisEntry => {
        if (typeof axisEntry[selectedValue] != "undefined") {
          return true;
        }
        let lookupValues = this.getAxisEntryLookupValues(axisEntry);
        return lookupValues.includes(selectedValue);
      });

      if (typeof match != "undefined" && typeof match[selectedValue] != "undefined") {
        return this.getAxisConfiguration(match[selectedValue]);
      }

      return this.getAxisConfiguration(match);
    },
    getSelectedMetricAxisConfiguration() {
      if (typeof this.chartData.metricAxis == "undefined" || this.chartData.metricAxis == null) {
        return {};
      }
      let selectedValue = this.chartData.metricAxis.value;
      if (typeof selectedValue == "undefined" || selectedValue == null || selectedValue == "") {
        return {};
      }

      if (!Array.isArray(this.vAxisForMetric)) {
        let axisConfiguration = this.vAxisForMetric[selectedValue];
        return this.getAxisConfiguration(axisConfiguration);
      }

      let match = this.vAxisForMetric.find(axisEntry => {
        if (typeof axisEntry[selectedValue] != "undefined") {
          return true;
        }
        let lookupValues = this.getAxisEntryLookupValues(axisEntry);
        return lookupValues.includes(selectedValue);
      });

      if (typeof match != "undefined" && typeof match[selectedValue] != "undefined") {
        return this.getAxisConfiguration(match[selectedValue]);
      }

      return this.getAxisConfiguration(match);
    },
    getChartOptions: function() {
      let showToolbox = !(ReportService.getReportAutoChartGenerateStatus() == "generate");
      let showTooltip = typeof this.eChartCriteria.showTooltip == "undefined" ? true : this.eChartCriteria.showTooltip;
      let showLegend = typeof this.eChartCriteria.showLegend == "undefined" ? true : this.eChartCriteria.showLegend;
      let showLabel = typeof this.eChartCriteria.showLabels == "undefined" ? false : this.eChartCriteria.showLabels;
      let hasMetric = typeof this.chartData.metricAxis != "undefined" && this.chartData.metricAxis != null;
      let xAxisIndexes = this.getChartableXAxisIndexes(this.chartData.names);
      let names = this.getChartableXAxisData(this.chartData.names, xAxisIndexes);
      let tierBoundaryIndexes = this.getChartableTierBoundaryIndexes(this.chartData.tierBoundaryIndexes, xAxisIndexes);
      let needValues = this.getChartableXAxisData(this.chartData.needValues, xAxisIndexes);
      let metricValues = this.getChartableXAxisData(this.chartData.metricValues, xAxisIndexes);
      let xAxis = RanksService.getNeedsMetricsXAxis(names, tierBoundaryIndexes);
      let primaryAxisConfiguration = this.getSelectedPerformanceAxisConfiguration();
      let metricAxisConfiguration = hasMetric ? this.getSelectedMetricAxisConfiguration() : {};
      let yAxis = [RanksService.getPrimaryNeedYAxis(this.chartData.needAxis.name, primaryAxisConfiguration)];
      let series = [
        RanksService.getNeedCircleSeries(
          this.chartData.needAxis.name,
          needValues,
          this.chartData.needAverage,
          this.chartData.needAverageColor
        )
      ];
      let needAverageSeries = RanksService.getAverageLineSeries(
        this.chartData.needAxis.name + " Avg",
        this.chartData.needAverage,
        this.chartData.needAverageColor,
        0,
        names.length
      );
      if (needAverageSeries != null) {
        series.push(needAverageSeries);
      }

      if (hasMetric) {
        yAxis.push(RanksService.getSecondaryMetricYAxis(this.chartData.metricAxis.name, metricAxisConfiguration));
        let metricSeries = RanksService.getMetricBarSeries(
          this.chartData.metricAxis.name,
          metricValues,
          this.chartData.metricAverage,
          this.chartData.metricAverageColor,
          metricAxisConfiguration
        );
        let zeroLine = RanksService.getZeroPercentMarkLine(metricValues);
        if (!$.isEmptyObject(zeroLine)) {
          metricSeries.markLine.data = metricSeries.markLine.data.concat(zeroLine.data);
        }
        series.push(metricSeries);
        let metricAverageSeries = RanksService.getAverageLineSeries(
          this.chartData.metricAxis.name + " Avg",
          this.chartData.metricAverage,
          this.chartData.metricAverageColor,
          1,
          names.length
        );
        if (metricAverageSeries != null) {
          series.push(metricAverageSeries);
        }
      }

      ScatterService.setShowSnap(this.isSavedCard);
      ScatterService.setShowToolbox(showToolbox);
      ScatterService.setShowLabel(showLabel);
      ScatterService.setShowLegend(showLegend);
      ScatterService.setShowTooltip(showTooltip);
      ScatterService.setChartType("ranks-needs-and-metrics");
      ScatterService.setLegendData(this.getLegendData(series));
      ScatterService.setGrid(RanksService.getNeedsMetricsGrid());
      ScatterService.setXAxis(xAxis);
      ScatterService.setYAxis(yAxis);
      ScatterService.setSeriesData(series);

      let options = ScatterService.getOptions(this, this.modalId, this.inputReportTypeName, this.inputDownloadFileName);
      options.tooltip.backgroundColor = "#ffffff";
      options.tooltip.formatter = params => RanksService.toolTipForNeedsMetricsRankChart(
        params,
        primaryAxisConfiguration,
        metricAxisConfiguration,
        hasMetric ? this.chartData.metricAxis.name : ""
      );
      options.tooltip.show = showTooltip;
      options.color = [this.chartData.needAverageColor, this.chartData.metricAverageColor];
      options.legend.orient = "vertical";
      options.legend.top = 70;
      options.legend.right = 10;
      options.legend.left = null;
      options.grid[0].top = 70;
      $.extend(true, options.toolbox, RanksService.getToolboxForAnalyticsChart());
      return options;
    },
    getLegendData(series) {
      return series.map(entry => { 
        if (this.isAverageSeries(entry)) {
          return {
            name: entry.name,
            icon: SymbolService.getDottedBarSymbol()
          };
        }
        return entry.name;
      });
    },
    isAverageSeries(series) {
      return series != null && typeof series.name == "string" && series.name.endsWith(" Avg");
    },
    getChartableXAxisIndexes(names) {
      return names.reduce((indexes, name, index) => {
        if (!this.isExcludedXAxisName(name)) {
          indexes.push(index);
        }
        return indexes;
      }, []);
    },
    getChartableXAxisData(values, xAxisIndexes) {
      if (!Array.isArray(values)) {
        return [];
      }
      return xAxisIndexes.map(index => values[index]);
    },
    getChartableTierBoundaryIndexes(tierBoundaryIndexes, xAxisIndexes) {
      if (!Array.isArray(tierBoundaryIndexes)) {
        return [];
      }
      return tierBoundaryIndexes.reduce((adjustedIndexes, tierBoundaryIndex) => {
        let adjustedIndex = xAxisIndexes.filter(index => index < tierBoundaryIndex).length;
        if (adjustedIndex > 0 && adjustedIndex < xAxisIndexes.length && !adjustedIndexes.includes(adjustedIndex)) {
          adjustedIndexes.push(adjustedIndex);
        }
        return adjustedIndexes;
      }, []);
    },
    isExcludedXAxisName(name) {
      return ["1W", "2W", "3W", "4W", "5W"].includes(name);
    },
    clearChart() {
      this.chartData = {};
      this.eChartCriteria = {};
      this.displayChart = false;
    },
    processChart: function(runChart = false, chartData = {}, eChartCriteria = {}) {
      if (runChart != true) {
        this.clearChart();
        return;
      }
      this.chartData = JSON.parse(JSON.stringify(chartData));
      this.eChartCriteria = JSON.parse(JSON.stringify(eChartCriteria));
      this.displayChart = this.chartData.names.length > 0;
      if (this.displayChart) {
        this.initChart();
        this.$emit("save-criteria", true);
      }
    }
  }
};
</script>
